/**
 * Currency formatting utilities.
 *
 * Canonical implementations consolidated from:
 * - packages/ssm-web5/src/utils/currency.js
 * - apps/ssm-web4/src/app/utils/currency/index.js
 *
 * NOTE: This contains only the pure `getStringAsCurrency` helper that
 * positions a currency symbol relative to a text value. App-specific
 * functions like `getNumberAsCurrency`, `setupCurrencyLocaleUser`, and
 * `getCurrency` remain in their respective apps because they depend on
 * global Dinero configuration / app state.
 */

import Dinero from 'dinero.js';

/**
 * Position a currency symbol around a text value, following the locale
 * conventions detected by Dinero.js.
 *
 * API decision: includes optional `currencyName` param (web4 superset).
 *
 * @param {string} text — the formatted number/string to wrap
 * @param {string} [currencySymbol=''] — symbol to display (e.g. "$", "€")
 * @param {string} [currencyName] — ISO 4217 code for Dinero formatting (default: "EUR")
 * @returns {string|undefined}
 */
export const getStringAsCurrency = (text, currencySymbol = '', currencyName) => {
    if (!text && text !== '') return;

    let currency = Dinero({
        amount: 99,
        currency: currencyName || 'EUR',
        precision: 0,
    }).toFormat();

    let symbol = currencySymbol || Dinero.defaultUserCurrencySymbol || '€';

    if (symbol === '') symbol = currency[0];

    if (symbol && symbol !== '€') {
        currency = currency.replace('€', symbol);
    }

    if (currency.indexOf(symbol) === 0) {
        return `${symbol} ${text}`;
    } else {
        // check if the character previous the symbol is a space to add it
        if (currency.length > 1 && /\s/.test(currency[currency.length - 2])) symbol = ` ${symbol}`;
        return `${text}${symbol}`;
    }
};
